import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { Button } from "../../components/Button";
import { Field, inputShell } from "../../components/Field";
import { OnboardingLayout } from "./OnboardingLayout";
import { requestOtp } from "../../api/auth";
import { ApiError } from "../../api/client";
import { readConsents, type SignupConsents } from "./ConsentScreen";

const MIN_PASSWORD = 6;

/* ------------------------------------------------------------------ */
/*  Session-scoped draft                                               */
/*                                                                     */
/*  When the student lands on the OTP screen and taps back, this       */
/*  component remounts. sessionStorage survives any back-navigation    */
/*  and is wiped when the tab closes, so nothing lingers.              */
/* ------------------------------------------------------------------ */
const DRAFT_KEY = "prepai.signup-draft";

interface Draft {
  email: string;
  nickname: string;
  password: string;
}

function loadDraft(): Partial<Draft> {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as Partial<Draft>) : {};
  } catch {
    return {};
  }
}

function saveDraft(draft: Draft): void {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    /* Private mode or storage full — the form still works, just blank on back. */
  }
}

/**
 * Screen 2 — email, nickname (optional) and password.
 *
 * Email is the identity key. A verification code is sent to it on submit.
 * Nickname is optional — used for greetings ("Good morning, Tunde").
 * No channel picker: code always goes to email.
 */
export function SignupScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  // Agreed on the screen before this one. Opened directly, or after the tab
  // forgot, there is nothing agreed to yet — back to the consents.
  const consents =
    ((location.state ?? {}) as { consents?: SignupConsents }).consents ?? readConsents();

  const draft = loadDraft();

  const [email, setEmail] = useState(draft.email ?? "");
  const [nickname, setNickname] = useState(draft.nickname ?? "");
  const [password, setPassword] = useState(draft.password ?? "");
  const [revealed, setRevealed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  // Persist the draft on every change so back-navigation restores it.
  useEffect(() => {
    saveDraft({ email, nickname, password });
  }, [email, nickname, password]);

  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Errors appear on submit, not on the first keystroke.
  const emailError =
    submitted && !email.trim()
      ? "Your email is required."
      : submitted && !emailLooksValid
        ? "Check the email address."
        : null;
  const passwordError =
    submitted && password.length > 0 && password.length < MIN_PASSWORD
      ? `Passwords need at least ${MIN_PASSWORD} characters.`
      : null;

  const ready = emailLooksValid && password.length >= MIN_PASSWORD;

  const submit = async () => {
    setSubmitted(true);
    if (!ready || sending) return;

    setSending(true);
    setRequestError(null);

    try {
      const { resendAfterSeconds } = await requestOtp(email.trim().toLowerCase(), {
        purpose: "signup",
      });

      navigate("/verify", {
        state: {
          email: email.trim().toLowerCase(),
          resendAfterSeconds,
          password,
          nickname: nickname.trim() || undefined,
          consents,
        },
      });
    } catch (error) {
      setRequestError(
        error instanceof ApiError ? error.message : "Couldn't send the code. Try again.",
      );
    } finally {
      setSending(false);
    }
  };

  if (!consents) return <Navigate to="/signup" replace />;

  return (
    <OnboardingLayout
      step={1}
      title="Let's get you started."
      subtitle="Three things, then you're practicing."
      action={
        <div className="flex flex-col gap-2">
          {requestError ? (
            <p role="alert" className="m-0 text-center text-[12.5px] text-negative">
              {requestError}
            </p>
          ) : null}
          <Button onClick={() => void submit()} disabled={(submitted && !ready) || sending}>
            {sending ? "Sending your code…" : "Continue"}
          </Button>
        </div>
      }
      footer={
        <p className="m-0 text-center text-[13px] text-text-muted">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-accent">
            Log in
          </Link>
        </p>
      }
    >
      <Field label="Email" htmlFor="email" error={emailError}>
        <div className={`${inputShell} ${emailError ? "border-negative" : "border-border-strong"}`}>
          <input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            aria-invalid={Boolean(emailError)}
            aria-describedby={emailError ? "email-error" : undefined}
            className="w-full bg-transparent text-[15px] text-text outline-none placeholder:text-text-muted"
            placeholder="you@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
      </Field>

      <Field label="Nickname (optional)" htmlFor="nickname" hint="What should we call you?">
        <div className={inputShell}>
          <input
            id="nickname"
            type="text"
            autoComplete="given-name"
            className="w-full bg-transparent text-[15px] text-text outline-none placeholder:text-text-muted"
            placeholder="e.g. Tunde"
            value={nickname}
            onChange={(event) => setNickname(event.target.value)}
            maxLength={50}
          />
        </div>
      </Field>

      <Field
        label="Password"
        htmlFor="password"
        error={passwordError}
        hint={`At least ${MIN_PASSWORD} characters`}
      >
        <div
          className={`${inputShell} justify-between ${passwordError ? "border-negative" : "border-border-strong"}`}
        >
          <input
            id="password"
            type={revealed ? "text" : "password"}
            autoComplete="new-password"
            aria-invalid={Boolean(passwordError)}
            aria-describedby={passwordError ? "password-error" : undefined}
            className="w-full bg-transparent text-[15px] text-text outline-none"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <button
            type="button"
            onClick={() => setRevealed((shown) => !shown)}
            aria-label={revealed ? "Hide password" : "Show password"}
            className="-m-2 shrink-0 p-2 text-text-muted"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"
                stroke="currentColor"
                strokeWidth="1.6"
              />
              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
            </svg>
          </button>
        </div>
      </Field>

      <p className="m-0 text-[12.5px] leading-[1.55] text-text-muted">
        Your email is your account — we'll send it a verification code next.
      </p>
    </OnboardingLayout>
  );
}
